import { useState, useCallback, useEffect } from 'react';
import { DocumentFile, ProcessingProgress, SUPPORTED_DOCUMENT_FORMATS, DocumentSettings } from '../types';
import JSZip from 'jszip';

export function useDocumentConverter() {
  const [documents, setDocuments] = useState<DocumentFile[]>([]);
  const [settings, setSettings] = useState<DocumentSettings>({
    format: 'pdf',
    quality: 'medium',
    preserveMetadata: true,
    optimizeForWeb: false
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState<ProcessingProgress>({ current: 0, total: 0 });

  const createDocumentPreview = useCallback((file: File): Promise<{ preview: string; pageCount?: number }> => {
    return new Promise((resolve) => {
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      
      // Simular contagem de páginas baseada no tamanho do arquivo
      const estimatedPages = Math.max(1, Math.floor(file.size / 50000)); // Aproximadamente 50KB por página
      
      // Criar preview baseado no tipo de documento
      let previewSvg = '';
      
      switch (extension) {
        case 'pdf':
          previewSvg = `
            <svg width="200" height="250" viewBox="0 0 200 250" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="200" height="250" rx="8" fill="#DC2626"/>
              <rect x="20" y="40" width="160" height="4" fill="white" opacity="0.9"/>
              <rect x="20" y="60" width="120" height="4" fill="white" opacity="0.7"/>
              <rect x="20" y="80" width="140" height="4" fill="white" opacity="0.7"/>
              <rect x="20" y="100" width="100" height="4" fill="white" opacity="0.7"/>
              <text x="100" y="30" text-anchor="middle" fill="white" font-family="Arial" font-size="16" font-weight="bold">PDF</text>
              <text x="100" y="200" text-anchor="middle" fill="white" font-family="Arial" font-size="12">${estimatedPages} páginas</text>
            </svg>
          `;
          break;
        case 'doc':
        case 'docx':
          previewSvg = `
            <svg width="200" height="250" viewBox="0 0 200 250" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="200" height="250" rx="8" fill="#2563EB"/>
              <rect x="20" y="40" width="160" height="3" fill="white" opacity="0.9"/>
              <rect x="20" y="55" width="140" height="3" fill="white" opacity="0.7"/>
              <rect x="20" y="70" width="150" height="3" fill="white" opacity="0.7"/>
              <rect x="20" y="85" width="120" height="3" fill="white" opacity="0.7"/>
              <rect x="20" y="100" width="160" height="3" fill="white" opacity="0.7"/>
              <text x="100" y="30" text-anchor="middle" fill="white" font-family="Arial" font-size="14" font-weight="bold">WORD</text>
              <text x="100" y="200" text-anchor="middle" fill="white" font-family="Arial" font-size="12">${estimatedPages} páginas</text>
            </svg>
          `;
          break;
        case 'odt':
          previewSvg = `
            <svg width="200" height="250" viewBox="0 0 200 250" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="200" height="250" rx="8" fill="#059669"/>
              <rect x="20" y="40" width="160" height="3" fill="white" opacity="0.9"/>
              <rect x="20" y="55" width="140" height="3" fill="white" opacity="0.7"/>
              <rect x="20" y="70" width="150" height="3" fill="white" opacity="0.7"/>
              <text x="100" y="30" text-anchor="middle" fill="white" font-family="Arial" font-size="14" font-weight="bold">ODT</text>
              <text x="100" y="200" text-anchor="middle" fill="white" font-family="Arial" font-size="12">${estimatedPages} páginas</text>
            </svg>
          `;
          break;
        case 'rtf':
          previewSvg = `
            <svg width="200" height="250" viewBox="0 0 200 250" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="200" height="250" rx="8" fill="#7C3AED"/>
              <rect x="20" y="40" width="160" height="3" fill="white" opacity="0.9"/>
              <rect x="20" y="55" width="140" height="3" fill="white" opacity="0.7"/>
              <text x="100" y="30" text-anchor="middle" fill="white" font-family="Arial" font-size="14" font-weight="bold">RTF</text>
            </svg>
          `;
          break;
        default:
          previewSvg = `
            <svg width="200" height="250" viewBox="0 0 200 250" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="200" height="250" rx="8" fill="#6B7280"/>
              <rect x="20" y="40" width="160" height="3" fill="white" opacity="0.9"/>
              <rect x="20" y="55" width="140" height="3" fill="white" opacity="0.7"/>
              <text x="100" y="30" text-anchor="middle" fill="white" font-family="Arial" font-size="12" font-weight="bold">${extension.toUpperCase()}</text>
            </svg>
          `;
      }
      
      const svgBlob = new Blob([previewSvg], { type: 'image/svg+xml' });
      const preview = URL.createObjectURL(svgBlob);
      
      setTimeout(() => {
        resolve({
          preview,
          pageCount: estimatedPages
        });
      }, 100);
    });
  }, []);

  const handleFileSelect = useCallback(async (files: FileList) => {
    console.log('Selecionando arquivos de documento:', files.length);
    
    const validFiles = Array.from(files).filter(file => {
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      const isValidFormat = SUPPORTED_DOCUMENT_FORMATS.includes(extension as any);
      
      if (!isValidFormat) {
        console.warn(`Formato não suportado: ${file.name}`);
      }
      
      return isValidFormat;
    });

    if (validFiles.length === 0) {
      console.warn('Nenhum arquivo válido selecionado');
      return;
    }

    const newDocuments: DocumentFile[] = [];
    
    for (const file of validFiles) {
      try {
        // Verificar se o arquivo já existe na lista (por nome, tamanho e última modificação)
        const isDuplicate = documents.some(existingDoc => 
          existingDoc.file.name === file.name && 
          existingDoc.file.size === file.size &&
          existingDoc.file.lastModified === file.lastModified
        );
        
        if (isDuplicate) {
          console.log(`Arquivo já existe na lista: ${file.name} (mesmo nome, tamanho e data)`);
          continue;
        }
        
        console.log(`Processando preview para: ${file.name}`);
        const { preview, pageCount } = await createDocumentPreview(file);
        const extension = file.name.split('.').pop()?.toLowerCase() || '';
        
        newDocuments.push({
          id: Math.random().toString(36).substr(2, 9),
          file,
          preview,
          pageCount,
          originalFormat: extension,
          status: 'pending',
          fileType: 'document'
        });
        
        console.log(`Documento adicionado: ${file.name}`);
      } catch (error) {
        console.error(`Erro ao processar arquivo ${file.name}:`, error);
      }
    }

    if (newDocuments.length > 0) {
      setDocuments(prev => [...prev, ...newDocuments]);
      console.log(`${newDocuments.length} documento(s) adicionado(s) com sucesso`);
    }
  }, [createDocumentPreview, documents]);

  // Simulação de conversão de documento (em uma implementação real, usaria uma biblioteca como pdf-lib)
  const convertDocument = useCallback((documentFile: DocumentFile): Promise<{ blob: Blob; format: string }> => {
    return new Promise((resolve, reject) => {
      // NOTA: Esta é uma implementação simulada
      // Em produção, você usaria bibliotecas como pdf-lib, docx, ou um serviço backend
      
      console.log(`🔄 Convertendo documento: ${documentFile.file.name}`);
      console.log(`📋 Configurações aplicadas:`, {
        formato: settings.format.toUpperCase(),
        qualidade: settings.quality,
        preservarMetadados: settings.preserveMetadata ? '✅ Ativado' : '❌ Desativado',
        otimizarParaWeb: settings.optimizeForWeb ? '✅ Ativado' : '❌ Desativado'
      });
      
      // Simular processamento baseado nas configurações
      const processingTime = settings.quality === 'maximum' ? 3000 : 
                           settings.quality === 'high' ? 2500 :
                           settings.quality === 'medium' ? 2000 : 1500;
      
      setTimeout(() => {
        try {
          const reader = new FileReader();
          reader.onload = () => {
            let arrayBuffer = reader.result as ArrayBuffer;
            
            // Simular otimizações baseadas nas configurações
            if (settings.optimizeForWeb) {
              console.log(`🌐 Aplicando otimização para web...`);
              // Simular redução de tamanho para web (75% do tamanho original)
              const reducedSize = Math.floor(arrayBuffer.byteLength * 0.75);
              const reducedBuffer = new ArrayBuffer(reducedSize);
              const reducedView = new Uint8Array(reducedBuffer);
              const originalView = new Uint8Array(arrayBuffer);
              
              // Copiar dados reduzidos (simulação)
              for (let i = 0; i < reducedSize; i++) {
                reducedView[i] = originalView[Math.floor(i * (arrayBuffer.byteLength / reducedSize))];
              }
              arrayBuffer = reducedBuffer;
              console.log(`📉 Tamanho reduzido em ${Math.round(25)}% para otimização web`);
            }
            
            if (settings.preserveMetadata) {
              console.log(`📊 Preservando metadados do documento original...`);
              // Em uma implementação real, aqui seriam preservados os metadados
            } else {
              console.log(`🗑️ Removendo metadados para reduzir tamanho...`);
            }
            
            // Simular diferentes tipos MIME baseados no formato de saída
            let mimeType = 'application/octet-stream';
            let qualityMultiplier = 1;
            
            switch (settings.format) {
              case 'pdf':
                mimeType = 'application/pdf';
                break;
              case 'docx':
                mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                break;
              case 'doc':
                mimeType = 'application/msword';
                break;
              case 'odt':
                mimeType = 'application/vnd.oasis.opendocument.text';
                break;
              case 'rtf':
                mimeType = 'application/rtf';
                break;
              case 'txt':
                mimeType = 'text/plain';
                // TXT é menor por natureza
                qualityMultiplier = 0.3;
                break;
            }
            
            // Simular impacto da qualidade no tamanho do arquivo
            switch (settings.quality) {
              case 'low':
                qualityMultiplier *= 0.6;
                break;
              case 'medium':
                qualityMultiplier *= 0.8;
                break;
              case 'high':
                qualityMultiplier *= 1.0;
                break;
              case 'maximum':
                qualityMultiplier *= 1.2;
                break;
            }
            
            // Aplicar modificações de qualidade
            if (qualityMultiplier !== 1) {
              const adjustedSize = Math.floor(arrayBuffer.byteLength * qualityMultiplier);
              const adjustedBuffer = new ArrayBuffer(adjustedSize);
              const adjustedView = new Uint8Array(adjustedBuffer);
              const originalView = new Uint8Array(arrayBuffer);
              
              for (let i = 0; i < adjustedSize; i++) {
                adjustedView[i] = originalView[Math.floor(i * (arrayBuffer.byteLength / adjustedSize))];
              }
              arrayBuffer = adjustedBuffer;
              
              const sizeChange = ((adjustedSize - documentFile.file.size) / documentFile.file.size * 100);
              console.log(`📏 Tamanho ajustado pela qualidade: ${sizeChange > 0 ? '+' : ''}${sizeChange.toFixed(1)}%`);
            }
            
            const blob = new Blob([arrayBuffer], { 
              type: mimeType 
            });
            
            console.log(`✅ Conversão concluída: ${documentFile.file.name} → ${settings.format.toUpperCase()}`);
            console.log(`📊 Tamanho final: ${(blob.size / 1024).toFixed(1)} KB`);
            
            resolve({ 
              blob, 
              format: settings.format 
            });
          };
          reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
          reader.readAsArrayBuffer(documentFile.file);
          
        } catch (error) {
          console.error(`❌ Erro na conversão:`, error);
          reject(new Error(`Erro na conversão: ${error}`));
        }
      }, processingTime + Math.random() * 1000); // Tempo variável baseado na qualidade
    });
  }, [settings]);

  const processDocument = useCallback(async (documentFile: DocumentFile) => {
    console.log('🔄 ProcessDocument INDIVIDUAL chamado para:', documentFile.file?.name);
    
    setDocuments(prev => prev.map(doc => 
      doc.id === documentFile.id ? { ...doc, status: 'processing' } : doc
    ));

    try {
      console.log('🔄 Chamando convertDocument para:', documentFile.file?.name);
      const { blob } = await convertDocument(documentFile);
      console.log('✅ ConvertDocument concluído para:', documentFile.file?.name, 'tamanho:', blob.size);
      
      // Criar um novo arquivo processado preservando o nome original (sem prefixos extras)
      const originalNameWithoutExt = documentFile.file.name.replace(/\.[^/.]+$/, '');
      const processedFile = new File(
        [blob],
        `${originalNameWithoutExt}.${settings.format}`,
        { type: blob.type }
      );
      
      console.log('✅ Atualizando estado para completed:', documentFile.file?.name);
      setDocuments(prev => prev.map(doc => 
        doc.id === documentFile.id 
          ? { 
              ...doc, 
              status: 'completed', 
              processedFile
            } 
          : doc
      ));
      console.log('✅ ProcessDocument concluído com sucesso para:', documentFile.file?.name);
    } catch (error) {
      console.error('❌ Erro ao processar documento:', documentFile.file?.name, error);
      setDocuments(prev => prev.map(doc => 
        doc.id === documentFile.id ? { ...doc, status: 'error' } : doc
      ));
    }
  }, [convertDocument, settings.format]);

  const processDocuments = useCallback(async (documentsToProcess?: DocumentFile[]) => {
    console.log('🚀 ProcessDocuments CHAMADO!', {
      documentsToProcessParam: documentsToProcess?.length || 'undefined',
      totalDocumentsInState: documents.length,
      documentsWithPendingStatus: documents.filter(doc => doc.status === 'pending').length,
      documentsWithFiles: documents.filter(doc => doc.file).length
    });

    // Filtrar apenas documentos válidos (com arquivo e status pending)
    const targetDocuments = documentsToProcess || documents.filter(doc => 
      doc.status === 'pending' && doc.file
    );
    
    console.log('🔄 ProcessDocuments: Iniciando processamento em lote', {
      totalDocuments: documents.length,
      targetDocuments: targetDocuments.length,
      documentsToProcess: documentsToProcess?.length || 'não especificado',
      documentosParaProcessar: targetDocuments.map(d => ({
        id: d.id,
        name: d.file?.name,
        status: d.status,
        hasFile: !!d.file
      }))
    });

    if (targetDocuments.length === 0) {
      console.log('⚠️ ProcessDocuments: Nenhum documento para processar');
      return;
    }

    console.log(`📄 ProcessDocuments: Iniciando conversão de ${targetDocuments.length} documento(s)`);
    setIsProcessing(true);
    setProgress({ current: 0, total: targetDocuments.length });

    for (let i = 0; i < targetDocuments.length; i++) {
      const documentFile = targetDocuments[i];
      console.log(`🔄 ProcessDocuments: Processando documento ${i + 1}/${targetDocuments.length}: ${documentFile.file.name}`);
      
      setProgress({ 
        current: i + 1, 
        total: targetDocuments.length, 
        currentFileName: documentFile.file.name 
      });

      try {
        await processDocument(documentFile);
        console.log(`✅ ProcessDocuments: Documento ${documentFile.file.name} processado com sucesso`);
      } catch (error) {
        console.error(`❌ ProcessDocuments: Erro ao processar documento ${documentFile.file.name}:`, error);
      }
    }

    console.log('🎉 ProcessDocuments: Processamento em lote concluído!');
    setIsProcessing(false);
    setProgress({ current: 0, total: 0 });
  }, [documents, processDocument]);

  const downloadDocument = useCallback((documentFile: DocumentFile) => {
    if (!documentFile.processedFile) return;

    const url = URL.createObjectURL(documentFile.processedFile);
    const a = document.createElement('a');
    a.href = url;
    a.download = documentFile.processedFile.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  const downloadAll = useCallback(() => {
    const completedDocuments = documents.filter(doc => doc.status === 'completed');
    completedDocuments.forEach(doc => downloadDocument(doc));
  }, [documents, downloadDocument]);

  const downloadAllAsZip = useCallback(async () => {
    const completedDocuments = documents.filter(doc => doc.status === 'completed');
    if (completedDocuments.length === 0) return;

    const zip = new JSZip();
    
    // Adicionar cada documento ao ZIP
    completedDocuments.forEach((documentFile) => {
      if (documentFile.processedFile) {
        const fileName = documentFile.processedFile.name;
        zip.file(fileName, documentFile.processedFile);
      }
    });

    // Gerar e baixar o ZIP
    try {
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `documentos_convertidos_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao criar ZIP:', error);
    }
  }, [documents]);

  const removeDocument = useCallback((id: string) => {
    console.log(`Removendo documento: ${id}`);
    
    // Encontrar o documento antes de removê-lo para limpeza de recursos
    const documentToRemove = documents.find(doc => doc.id === id);
    
    if (documentToRemove) {
      // Limpar URLs de objeto para evitar vazamentos de memória
      if (documentToRemove.preview && documentToRemove.preview.startsWith('blob:')) {
        URL.revokeObjectURL(documentToRemove.preview);
      }
      
      if (documentToRemove.processedFile) {
        console.log(`Limpando arquivo processado: ${documentToRemove.processedFile.name}`);
      }
      
      console.log(`Documento removido: ${documentToRemove.file.name}`);
    }
    
    setDocuments(prev => prev.filter(doc => doc.id !== id));
  }, [documents]);

  const clearAll = useCallback(() => {
    console.log('Limpando todos os documentos');
    
    // Limpar todos os URLs de objeto para evitar vazamentos de memória
    documents.forEach(document => {
      if (document.preview && document.preview.startsWith('blob:')) {
        URL.revokeObjectURL(document.preview);
      }
    });
    
    setDocuments([]);
    console.log('Todos os documentos foram removidos');
  }, [documents]);

  const updateSettings = useCallback((key: keyof DocumentSettings, value: any) => {
    console.log(`⚙️ Configuração alterada: ${key} = ${value}`);
    
    // Logs específicos para toggles
    if (key === 'preserveMetadata') {
      console.log(`📊 Preservar Metadados: ${value ? '✅ ATIVADO' : '❌ DESATIVADO'}`);
      console.log(`   ${value ? 'Informações de autor, data e propriedades serão mantidas' : 'Metadados serão removidos para reduzir tamanho'}`);
    }
    
    if (key === 'optimizeForWeb') {
      console.log(`🌐 Otimizar para Web: ${value ? '✅ ATIVADO' : '❌ DESATIVADO'}`);
      console.log(`   ${value ? 'Arquivo será comprimido para compartilhamento online' : 'Tamanho original será mantido'}`);
    }
    
    if (key === 'format') {
      console.log(`📄 Formato alterado para: ${value.toUpperCase()}`);
    }
    
    if (key === 'quality') {
      console.log(`🎚️ Qualidade alterada para: ${value.toUpperCase()}`);
    }
    
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  // Debug: Monitorar mudanças no estado dos documentos
  useEffect(() => {
    console.log('Estado dos documentos atualizado:', {
      total: documents.length,
      pending: documents.filter(d => d.status === 'pending').length,
      processing: documents.filter(d => d.status === 'processing').length,
      completed: documents.filter(d => d.status === 'completed').length,
      error: documents.filter(d => d.status === 'error').length,
      documentNames: documents.map(d => d.file.name)
    });
  }, [documents]);

  return {
    documents,
    settings,
    isProcessing,
    progress,
    handleFileSelect,
    processDocument,
    processDocuments,
    downloadDocument,
    downloadAll,
    downloadAllAsZip,
    removeDocument,
    clearAll,
    updateSettings
  };
}